import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, X, Maximize2, Mic, Square, Send, Loader2, CheckCircle2,
         XCircle, AlertTriangle, ArrowRight, Eraser } from "lucide-react";
import { useAssistantContext, QUICK_ACTIONS } from "@/lib/assistantContext";
import { useModalBack } from "@/lib/modalBack";
import AssistantUpdate from "@/components/AssistantUpdate";

function pickMime() {
  const MR = window.MediaRecorder;
  if (!MR) return null;
  for (const t of ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"]) {
    if (MR.isTypeSupported?.(t)) return t;
  }
  return null;
}

// Global Project Assistant panel: right-side drawer on desktop (~400px),
// full-screen sheet on mobile. Stays mounted once opened so conversation
// and draft input survive open/close. Never navigates away from the page.
export default function AssistantPanel({ open, onClose, init }) {
  useModalBack(open, onClose); // phone Back closes the 360 panel (Round 10 #1)
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const ctx = useAssistantContext();
  const [mode, setMode] = useState("chat"); // chat | update (360, voice-first)
  const [updateCtx, setUpdateCtx] = useState(null); // { projectId, projectName } from opener
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [listening, setListening] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const idRef = useRef(0);
  const reviewMsgRef = useRef(null); // chat message to rewrite after a 360 approve
  const scrollRef = useRef(null);

  const chips = QUICK_ACTIONS[ctx.page] || QUICK_ACTIONS.generic;

  useEffect(() => {
    if (open) setTimeout(() => scrollRef.current?.scrollTo({ top: 999999, behavior: "smooth" }), 60);
  }, [messages, open]);

  useEffect(() => {
    if (!open) return;
    if (init?.mode === "update") {
      setUpdateCtx({ projectId: init.projectId || null, projectName: init.projectName || "" });
      setMode("update");
    } else {
      setMode("chat");
    }
  }, [open, init]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const patchMsg = (id, patch) =>
    setMessages((ms) => ms.map((m) => (m.id === id ? { ...m, ...patch } : m)));

  const submit = async (raw) => {
    const text = (raw ?? input).trim();
    if (!text || busy) return;
    setBusy(true);
    setInput("");
    push({ role: "user", text });
    const mid = ++idRef.current;
    try {
      // Typed project updates go through the full 360 engine — one review with
      // every draft type + Approve all, same as voice. Questions (no sections)
      // fall through to the chat engine below.
      if (ctx.projectId) {
        push({ id: mid, role: "a", text: t("ap_working"), working: true });
        try {
          const { data } = await api.post("/update360/interpret", {
            transcript: text,
            lang: i18n.language?.startsWith("es") ? "es" : "en",
            local_date: new Date().toLocaleDateString("en-CA"),
            context_project_id: ctx.projectId,
          }, { timeout: 90000 });
          if (data.sections && Object.keys(data.sections).length > 0) {
            patchMsg(mid, { working: false, text: t("ap_opened_review") });
            reviewMsgRef.current = mid;
            setUpdateCtx({ projectId: ctx.projectId, projectName: ctx.label || "", initialText: text, initialDraft: data });
            setMode("update");
            return;
          }
        } catch { /* fall through to the chat engine */ }
        patchMsg(mid, { working: false });
      }
      const { data } = await api.post("/bg/interpret", {
        transcript: text, original_transcript: text,
        local_date: new Date().toLocaleDateString("en-CA"), lang: i18n.language,
        context_project_id: ctx.projectId, context_customer_id: ctx.customerId,
        context_page: ctx.page, selected_photo_ids: ctx.selectedPhotoIds,
      }, { timeout: 90000 });
      if (data.unsupported && !data.card) {
        push({ id: mid, role: "a", text: data.speak || t("bgv_no_results") });
      } else if (!data.card) {
        push({ id: mid, role: "a", text: data.speak || t("bgv_no_results") });
      } else {
        push({ id: mid, role: "a", text: data.speak || "", card: data.card });
      }
    } catch (e) {
      const d = String(e.response?.data?.detail || "");
      push({ id: mid, role: "a", text: d.startsWith("perm_denied") ? t("bgv_perm_denied") : t("bg_error"), error: true });
    } finally { setBusy(false); }
  };

  const push = (m) => setMessages((ms) => [...ms, { id: m.id ?? ++idRef.current, ...m }]);

  const choose = async (msg, choiceId) => {
    try {
      const { data } = await api.post(`/bg/actions/${msg.card.action_id}/choose`, { choice_id: choiceId });
      patchMsg(msg.id, { card: data.card });
    } catch (e) { toast.error(t("bg_error")); }
  };

  const undoResult = async (msg) => {
    try {
      await api.post(`/schedule/move/${msg.result.undo_id}/undo`);
      setMessages((s) => s.map((x) => x.id === msg.id
        ? { ...x, result: { ...x.result, undone: true, message: `${x.result.message} (${t("ap_undone")})` } } : x));
    } catch { /* toast-free: button stays so the user can retry */ }
  };


  const confirm = async (msg, ack = false) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/bg/actions/${msg.card.action_id}/confirm`, {
        method: "tap", lang: i18n.language, local_ts: new Date().toISOString(), ack_conflicts: ack,
      }, { timeout: 60000 });
      patchMsg(msg.id, { card: null, result: data, text: "" });
    } catch (e) {
      const d = String(e.response?.data?.detail || "");
      if (d === "conflict_ack_required") patchMsg(msg.id, { needAck: true });
      else toast.error(d.startsWith("perm_denied") ? t("bgv_perm_denied") : t("bgv_failed"));
    } finally { setBusy(false); }
  };

  const cancel = async (msg) => {
    if (msg.card?.action_id) {
      try { await api.post(`/bg/actions/${msg.card.action_id}/cancel`); } catch (e) { /* no-op */ }    }
    patchMsg(msg.id, { card: null, text: t("action_cancel"), cancelled: true });
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickMime();
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data?.size && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((tr) => tr.stop());
        setListening(false);
        const blob = new Blob(chunksRef.current, { type: mime || "audio/webm" });
        if (blob.size < 500) return;
        const ext = (mime || "").includes("mp4") ? "m4a" : "webm";
        const fd = new FormData();
        fd.append("lang", i18n.language?.startsWith("es") ? "es" : "en");
        fd.append("file", new File([blob], `voice.${ext}`, { type: blob.type }));
        setBusy(true);
        try {
          const { data } = await api.post("/stt", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
          if (data.text) setInput((v) => (v ? `${v} ${data.text}` : data.text));
        } catch (e) { toast.error(t("toast_transcribe_failed")); }
        finally { setBusy(false); }
      };
      recRef.current = rec;
      rec.start();
      setListening(true);
    } catch (e) { toast.error(t("err_loc_denied")); }
  };

  const stopRec = () => { try { recRef.current?.stop(); } catch (e) { /* no-op */ } };

  const expand = () => {
    onClose();
    navigate(`/app/assistant/voice${ctx.projectId ? `?project=${ctx.projectId}` : ""}`);
  };

  const goResult = (path) => { onClose(); navigate(path); };

  return (
    <div data-testid="assistant-panel" role="dialog" aria-modal="true" aria-label={t("ap_title")}
      className={`fixed inset-0 z-[70] flex flex-col bg-white transition-all duration-200 sm:inset-y-0 sm:left-auto sm:right-0 sm:w-[400px] sm:border-l sm:border-slate-200 sm:shadow-2xl ${open ? "" : "invisible translate-x-full"}`}>
      {/* Header */}
      <div className="border-b border-slate-200 px-4 pb-3" style={{ paddingTop: "max(env(safe-area-inset-top, 0px), 12px)" }}>
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-brand text-white">
            <Sparkles className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-extrabold text-slate-900" data-testid="assistant-panel-title">{t("ap_title")}</div>
            {ctx.label ? (
              <span className="mt-0.5 inline-flex max-w-full items-center gap-1 rounded-full bg-brand/10 px-2 py-0.5 text-[11px] font-bold text-branddeep" data-testid="assistant-context">
                <span className="truncate">{t("ap_helping", { name: ctx.label })}</span>
                <button onClick={ctx.clearContext} aria-label={t("ap_clear_context")} data-testid="assistant-context-clear"
                  className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full hover:bg-brand/20">
                  <X className="h-3 w-3" />
                </button>
              </span>
            ) : (
              <span className="text-[11px] text-slate-400" data-testid="assistant-context">{t("ap_general")}</span>
            )}
          </div>
          <button onClick={expand} aria-label={t("ap_expand")} data-testid="assistant-expand"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 active:scale-95">
            <Maximize2 className="h-4 w-4" />
          </button>
          <button onClick={onClose} aria-label={t("ap_close")} data-testid="assistant-close"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 active:scale-95">
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {mode === "update" ? (
        <AssistantUpdate projectId={updateCtx?.projectId || ctx.projectId}
          projectName={updateCtx?.projectName || ctx.label || ""} onBack={() => setMode("chat")}
          onClosePanel={onClose}
          onScheduleCommand={(txt) => { setUpdateCtx(null); setMode("chat"); setTimeout(() => submit(txt), 0); }}
          onSaved={(txt) => { if (reviewMsgRef.current) patchMsg(reviewMsgRef.current, { text: txt }); }}
          initialText={updateCtx?.initialText || ""} initialDraft={updateCtx?.initialDraft || null} />
      ) : (
      <>
      {/* Conversation */}
      <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4" data-testid="assistant-messages">
        {messages.length === 0 && (
          <p className="rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-600" data-testid="assistant-welcome">{t("ap_welcome")}</p>
        )}
        {messages.map((m) => (
          <div key={m.id} className={m.role === "user" ? "flex justify-end" : ""} data-testid={`assistant-msg-${m.id}`}>
            {m.role === "user" ? (
              <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-brand px-4 py-2.5 text-sm font-semibold text-white">{m.text}</div>
            ) : (
              <div className="max-w-full space-y-2">
                {m.text && !m.result && (
                  <div className={`rounded-2xl rounded-bl-sm px-4 py-2.5 text-sm ${m.error ? "bg-red-50 text-red-700" : m.cancelled ? "bg-slate-50 text-slate-400" : "bg-slate-100 text-slate-800"}`}
                    data-testid={`assistant-text-${m.id}`}>{m.text}</div>
                )}
                {m.card && (
                  <div className="space-y-3 rounded-2xl border-2 border-brand/30 bg-white p-4" data-testid={`assistant-card-${m.id}`}>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-brand">{t(`bgv_review_${m.card.kind}`, m.card.kind)}</div>
                    {m.card.results ? (
                      <div className="space-y-1.5">
                        {m.card.results.length === 0 && <p className="text-sm text-slate-500">{t("bgv_no_results")}</p>}
                        {m.card.results.map((r, i) => (
                          r.path ? (
                            <button key={r.id || i} onClick={() => goResult(r.path)} data-testid={`assistant-result-${r.id || i}`}
                              className="flex w-full items-center justify-between gap-2 rounded-xl border border-slate-200 px-3.5 py-2.5 text-left min-h-[44px] active:scale-[0.99]">
                              <span className="min-w-0"><span className="block truncate text-sm font-bold text-slate-900">{r.name}</span>
                                <span className="block text-xs text-slate-500 line-clamp-2">{r.sub}</span></span>
                              <ArrowRight className="h-4 w-4 shrink-0 text-slate-400" />
                            </button>
                          ) : (
                            <div key={r.id || i} className="rounded-xl border border-slate-200 px-3.5 py-2.5" data-testid={`assistant-result-${r.id || i}`}>
                              <span className="block text-sm font-bold text-slate-900">{r.name}</span>
                              <span className="block text-xs text-slate-500">{r.sub}</span>
                            </div>
                          )
                        ))}
                        {m.card.total > m.card.results.length && (
                          <p className="text-xs font-semibold text-slate-500" data-testid={`assistant-total-${m.id}`}>
                            {t("ap_total", { count: m.card.total })}
                          </p>
                        )}
                        {m.card.kind === "help" && m.card.results.length > 0 && (
                          <button
                            onClick={() => {
                              const hit = m.card.results[0];
                              if (hit.id === "photos_upload") { window.dispatchEvent(new Event("bf:open-camera")); return; }
                              const dest = { project_create: "/app/projects/new", schedule_gantt: "/app/schedule?add=1",
                                crew: "/app/crew?invite=1", estimates: "/app/financials", customers: "/app/customers",
                                billing: "/app/billing", website: "/app/website", time: "/app/time",
                                offline: "/app/app-updates" }[hit.id];
                              goResult(dest || hit.path);
                            }}
                            data-testid={`assistant-doit-${m.id}`}
                            className="mt-1 inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-full bg-brand px-4 text-xs font-bold text-white active:scale-95">
                            {t("ap_do_it")}
                          </button>
                        )}
                      </div>
                    ) : (
                      <>
                        {m.card.kind === "answer" && (
                          <div className="space-y-2" data-testid={`assistant-answer-${m.id}`}>
                            <p className="whitespace-pre-wrap text-sm text-slate-800">
                              {String(m.card.text || "").split(/(\[\[NAV:[^\]]+\]\])/g).map((part, i) => {
                                const nav = part.match(/^\[\[NAV:([^\]|]+)\|([^\]]+)\]\]$/);
                                return nav ? (
                                  <button key={i} onClick={() => goResult(nav[1])} data-testid={`assistant-answer-${m.id}-nav-${i}`}
                                    className="mx-0.5 inline-flex items-center gap-1 rounded-full bg-brand/10 px-2.5 py-0.5 text-xs font-bold text-branddeep active:scale-95">
                                    {nav[2]}
                                  </button>
                                ) : <span key={i}>{part}</span>;
                              })}
                            </p>
                            {(m.card.followups || []).length > 0 && (
                              <div className="flex flex-wrap gap-1.5">
                                {m.card.followups.map((f, i) => (
                                  <button key={i} onClick={() => submit(f)} data-testid={`assistant-followup-${m.id}-${i}`}
                                    className="min-h-[36px] rounded-full border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 active:scale-95">{f}</button>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                        {m.card.choices?.length > 0 && (
                          <div className="space-y-1.5" data-testid={`assistant-choices-${m.id}`}>
                            <p className="text-xs font-semibold text-slate-700">{t("bgv_choose")}</p>
                            {m.card.choices.map((c) => (
                              <button key={c.id} onClick={() => choose(m, c.id)} data-testid={`assistant-choice-${c.id}`}
                                className="flex w-full items-center justify-between rounded-xl border border-slate-300 px-3.5 py-2.5 text-left min-h-[44px] active:scale-[0.99]">
                                <span className="text-sm font-bold text-slate-900">{c.label}</span>
                                <span className="text-xs text-slate-500">{c.id === "create_new" ? t("bgv_create_new_customer") : c.sub}</span>
                              </button>
                            ))}
                          </div>
                        )}
                        <div className="divide-y divide-slate-100">
                          {(m.card.rows || []).map((r) => r.key === "project" ? (
                            <div key={r.key} className="rounded-xl bg-brand/10 px-3.5 py-2.5" data-testid={`assistant-row-${r.key}`}>
                              <span className="block text-[10px] font-bold uppercase tracking-widest text-branddeep">{t("bgv_row_project")}</span>
                              <span className="block text-base font-extrabold text-slate-900">{r.value}</span>
                            </div>
                          ) : (
                            <div key={r.key} className="flex items-start justify-between gap-3 py-1.5" data-testid={`assistant-row-${r.key}`}>
                              <span className="shrink-0 text-[10px] font-bold uppercase tracking-widest text-slate-400">{t(`bgv_row_${r.key}`, r.key)}</span>
                              <span className="text-right text-sm font-semibold text-slate-900 whitespace-pre-wrap">{r.value}</span>
                            </div>
                          ))}
                        </div>
                        {(m.card.conflicts || []).length > 0 && (
                          <div className="rounded-xl border border-red-200 bg-red-50 p-2.5 space-y-1" data-testid={`assistant-conflicts-${m.id}`}>
                            {m.card.conflicts.map((cf, i) => (
                              <div key={i} className="flex items-center gap-2 text-xs font-bold text-red-700">
                                <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> {cf}
                              </div>
                            ))}
                          </div>
                        )}
                        {(m.card.missing || []).length > 0 && (
                          <p className="text-xs font-semibold text-red-600" data-testid={`assistant-missing-${m.id}`}>
                            {t("bgv_missing")}: {m.card.missing.map((x) => t(`bgv_row_${x}`, x)).join(", ")}
                          </p>
                        )}
                        {m.needAck && <p className="text-xs font-semibold text-red-600">{t("bgv_need_ack")}</p>}
                        {(m.card.confirmable || m.needAck) && (
                        <div className="flex flex-wrap gap-2">
                          {m.needAck ? (
                            <button onClick={() => confirm(m, true)} disabled={busy} data-testid={`assistant-ack-${m.id}`}
                              className="inline-flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-full bg-red-600 px-4 text-xs font-bold text-white active:scale-95 disabled:opacity-50">
                              <AlertTriangle className="h-4 w-4" /> {t("bgv_ack_conflicts")}
                            </button>
                          ) : (
                            <button onClick={() => confirm(m)} disabled={busy || !m.card.confirmable} data-testid={`assistant-confirm-${m.id}`}
                              className="inline-flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-full bg-green-600 px-4 text-xs font-bold text-white active:scale-95 disabled:opacity-50">
                              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} {t(["move_task", "delay_task", "complete_task", "rain_day", "assign_task", "add_delivery"].includes(m.card.kind) ? "bgv_apply" : "bgv_confirm")}
                            </button>
                          )}
                          <button onClick={() => cancel(m)} data-testid={`assistant-cancel-${m.id}`}
                            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full border border-slate-300 px-4 text-xs font-bold text-slate-600 active:scale-95">
                            <XCircle className="h-4 w-4" /> {t("action_cancel")}
                          </button>
                        </div>
                        )}
                      </>
                    )}
                  </div>
                )}
                {m.result && (
                  <div className={`space-y-2 rounded-2xl border-2 p-4 ${m.result.pending_approval ? "border-amber-300 bg-amber-50" : "border-green-300 bg-green-50"}`} data-testid={`assistant-done-${m.id}`}>
                    <div className={`flex items-center gap-2 text-xs font-bold ${m.result.pending_approval ? "text-amber-800" : "text-green-800"}`}>
                      <CheckCircle2 className="h-4 w-4" /> {m.result.pending_approval ? t("bgv_pending_approval") : t("bgv_success")}
                    </div>
                    <p className={`text-sm whitespace-pre-wrap ${m.result.pending_approval ? "text-amber-900" : "text-green-900"}`}>{m.result.message}</p>
                    <div className="flex flex-wrap gap-2">
                      {(m.result.links || []).map((l, i) => (
                        <button key={i} onClick={() => goResult(l.path)} data-testid={`assistant-open-${m.id}-${i}`}
                          className="inline-flex min-h-[40px] items-center gap-1.5 rounded-full bg-brand px-4 text-xs font-bold text-white active:scale-95">
                          {t(`bgv_${l.key}`, l.key)} <ArrowRight className="h-3.5 w-3.5" />
                        </button>
                      ))}
                      {m.result.undo_id && !m.result.undone && (
                        <button onClick={() => undoResult(m)} data-testid={`assistant-undo-${m.id}`}
                          className="inline-flex min-h-[40px] items-center gap-1.5 rounded-full border border-slate-300 px-4 text-xs font-bold text-slate-600 active:scale-95">
                          {t("ap_undo")}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Quick actions + input */}
      <div className="border-t border-slate-200 px-4 pt-2" style={{ paddingBottom: "max(env(safe-area-inset-bottom, 0px), 10px)" }}>
        <div className="mb-2 flex items-center gap-2 overflow-x-auto bf-no-scrollbar" data-testid="assistant-quick-actions">
          <button onClick={() => { setUpdateCtx(null); setMode("update"); }} data-testid="assistant-quick-360"
            className="shrink-0 inline-flex min-h-[36px] items-center gap-1.5 rounded-full bg-slateink px-3.5 py-2 text-[11px] font-bold text-white active:scale-95">
            <Mic className="h-3.5 w-3.5" /> {t("ap_start_360")}
          </button>
          {chips.map((c) => (
            <button key={c} onClick={() => setInput(t(`ap_qa_${c}_sample`))} data-testid={`assistant-quick-${c}`}
              className="shrink-0 rounded-full border border-brand/40 bg-brand/5 px-3.5 py-2 text-[11px] font-bold text-brand active:scale-95 min-h-[36px]">
              {t(`ap_qa_${c}`)}
            </button>
          ))}
          {messages.length > 0 && (
            <button onClick={() => setMessages([])} data-testid="assistant-clear" aria-label={t("ap_clear")}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-slate-300 text-slate-500 active:scale-95">
              <Eraser className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="flex items-end gap-2">
          <textarea rows={1} value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }}
            data-testid="assistant-input" placeholder={t("ap_placeholder")}
            className="max-h-28 flex-1 resize-none rounded-2xl border border-slate-300 px-4 py-3 text-sm text-slate-900" />
          <button onClick={() => (listening ? stopRec() : startRec())} disabled={busy}
            data-testid="assistant-mic" aria-label={listening ? t("ap_stop") : t("ap_tap_mic")}
            className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-white active:scale-90 disabled:opacity-50 ${listening ? "bg-red-500 animate-pulse" : "bg-slateink"}`}>
            {listening ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
          </button>
          <button onClick={() => submit()} disabled={busy || !input.trim()} data-testid="assistant-send" aria-label={t("ap_send")}
            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-brand text-white active:scale-90 disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </button>
        </div>
      </div>
      </>
      )}
    </div>
  );
}
